import React, { useRef } from 'react';

import PropTypes from 'prop-types';
import { Button, Card, Icon } from 'semantic-ui-react';

import clsx from 'clsx';
import _isEmpty from 'lodash/isEmpty';
import _isNil from 'lodash/isNil';

import { ChildrenPropType, ObjectPropType } from 'libs/proptypes';

import { getImageStyle, getFileInput, getFileEditorModal } from './EditFileField.utils';
import FileEditorModal from './components/FileEditorModal';
import FileInput from './components/FileInput';
import { getImageUrl } from '../../services/files';

import './EditFileField.scss';

const propTypes = {
  url: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
  onChange: PropTypes.func,
  onUpload: PropTypes.func,
  onDelete: PropTypes.func,
  className: PropTypes.string,
  imageChildren: ChildrenPropType,
  hideButtons: PropTypes.bool,
  borderless: PropTypes.bool,
  imageStyle: ObjectPropType,
  uploadButtonProps: ObjectPropType,
  children: ChildrenPropType,
};

const defaultProps = {
  url: '',
  onChange: null,
  onUpload: null,
  className: '',
  onDelete: null,
  imageChildren: null,
  hideButtons: false,
  borderless: false,
  imageStyle: {},
  uploadButtonProps: {},
  children: null,
};

function EditFileField({
  className,
  url,
  onChange,
  onUpload,
  onDelete,
  imageChildren,
  hideButtons,
  borderless,
  imageStyle: imageStyleProp,
  uploadButtonProps,
  children,
}) {
  const fileInputRef = useRef(null);
  const fileEditorModalRef = useRef(null);
  const finalClassName = clsx('edit-file-field', className, { borderless });

  const imageUrl = getImageUrl(url);
  const imageStyle = getImageStyle(imageUrl, imageStyleProp);
  const hasFile = !_isEmpty(imageUrl);

  const fileInput = getFileInput(children, fileInputRef, onChange);
  const hasFileInput = !_isNil(fileInput);

  const fileEditorModal = getFileEditorModal(children, fileEditorModalRef, imageUrl, onChange);
  const hasFileEditorModal = !_isNil(fileEditorModal);

  const handleMainClick = () => {
    if (hasFileInput) {
      // Use file input for file selection
      if (fileInputRef.current) {
        fileInputRef.current.click();
      }
    } else if (hasFile && onChange) {
      onChange();
    } else if (!hasFile && onUpload) {
      onUpload();
    }
  };

  function onEditFileWithEditorClick(e) {
    e.stopPropagation();
    if (fileEditorModalRef.current) {
      fileEditorModalRef.current.openModal();
    }
  }

  return (
    <Card className={finalClassName}>
      <Card.Content
        className={clsx('image-container', { placeholder: !hasFile })}
        onClick={handleMainClick}
      >
        {hasFile ? (
          <div className="image" style={imageStyle}>
            {!_isNil(imageChildren) && imageChildren}
            {hasFileEditorModal && (
              <Button
                icon="pencil"
                className="edit-file-with-editor-button"
                onClick={onEditFileWithEditorClick}
              />
            )}
          </div>
        ) : (
          <div className="placeholder-content">
            <div className="upload-icon">
              <Icon name="image outline" />
            </div>
            <div className="upload-text">Click to select image</div>
          </div>
        )}
      </Card.Content>
      {!hideButtons && (
        <Card.Content extra className="buttons">
          {hasFile ? (
            <>
              <Button basic color="green" fluid icon="edit" onClick={handleMainClick} />
              {!_isNil(onDelete) && (
                <Button basic color="red" fluid icon="delete" onClick={onDelete} />
              )}
            </>
          ) : (
            <Button
              basic
              fluid
              icon="plus"
              content="Select Image"
              onClick={handleMainClick}
              {...uploadButtonProps}
            />
          )}
        </Card.Content>
      )}
      {fileInput}
      {fileEditorModal}
    </Card>
  );
}

EditFileField.FileInput = FileInput;
EditFileField.FileEditorModal = FileEditorModal;

EditFileField.propTypes = propTypes;
EditFileField.defaultProps = defaultProps;

export default EditFileField;
